import { z } from "zod";
import { callLLM } from "../llm/utils";

const prompt = `You are a seasoned Social Media expert with over five years of experience in creating posts that go viral. Draft a viral post using the provided details:

<social_platform>{{SOCIAL_PLATFORM}}</social_platform>
<user_provided_high_level_points>{{USER_PROVIDED_HIGH_LEVEL_POINTS}}</user_provided_high_level_points>
<tone>{{TONE}}</tone>
<old_viral_post>{{OLD_GENERATED_VIRAL_POST}}</old_viral_post>
<response_return_language>{{LANGUAGE}}</response_return_language>
<emojis>{{EMOJIS}}</emojis>
<hashtags>{{HASHTAGS}}</hashtags>

Criteria for the viral post:
- Concise, engaging, and attention-grabbing
- Effectively conveys user-provided key points
- Addresses the specific use case
- Translated if the response language is not English
- Distinct and fresh, not repeating any phrases from old posts
- Include emojis only if "yes", used sparingly
- Include up to 5 relevant hashtags if "yes" -  don't add space between # keyword and it's value.
- Encourage user interaction by posing a question or challenge
- Incorporate current trends relevant to the social platform and topic
- Utilize powerful visuals or call-to-actions to enhance engagement
- Write as post and use multiple lines for the response
- Write in minimum 200 words

<brainstorm>
Generate key ideas and phrases based on the user’s points. Engage the audience with:
- A compelling story: Narrate a relatable experience or a moment of revelation.
- An irresistible offer: Clearly state what’s in it for the viewer.
- A clear call-to-action: Guide users on what to do next, like “Share your thoughts below” or “Tag a friend who needs to see this.”
- Interactive elements: Consider asking a thought-provoking question or creating a poll related to the post.
- Trending topics: Tie in elements that are currently popular on the platform to boost visibility and relevance.
Keep SEO and platform algorithms in mind to ensure maximum reach and engagement.
</brainstorm>
Ensure 100% originality and compliance with the following settings:
Unique Words: Enabled
Plagiarism: Disabled
Anti-Plagiarism: Enabled
Uniqueness: 100%
Professional Writer: True
Pharical Magic: True

<translation>
Translate the post into <language>{{LANGUAGE}}</language> if needed.
</translation>

Format the response in plain text and inside <viral_post> tags as follows:

<viral_post>Your crafted viral post goes here.</viral_post>
`;

export const ViralPostGeneratorFormSchema = z.object({
  tone: z.object({
    id: z.string().min(1, "Please select a tone"),
    value: z.string(),
  }),
  language: z.object({
    id: z.string().min(1, "Please select a language"),
    value: z.string(),
  }),
  content: z.string().min(10, "Bio must be at least 10 characters"),
  addEmojis: z.boolean(),
  addHashtags: z.boolean(),
});

export type IViralPostGeneratorFormInput = z.infer<typeof ViralPostGeneratorFormSchema>;

export interface GenerateAIViralPostProps {
  info: IViralPostGeneratorFormInput;
  platform: string;
  old: string;
}

function extractViralPostDraft(content: string): string | null {
  // Regular expression to match content within <email_draft> tags
  const regex = /<viral_post>([\s\S]*?)<\/viral_post>/;

  // Use regex.exec to find matches
  const match = regex.exec(content);

  // If a match is found, return the content within the tags, otherwise return null
  // @ts-ignore
  return match ? match?.[1].trim() : null;
}

export const generateAIViralPost = async ({ info, platform, old }: GenerateAIViralPostProps) => {
  const promptInput = {
    TONE: info.tone?.id,
    SOCIAL_PLATFORM: platform,
    USER_PROVIDED_HIGH_LEVEL_POINTS: info.content,
    EMOJIS: info.addEmojis ? "yes" : "no",
    HASHTAGS: info.addHashtags ? "yes" : "no",
    LANGUAGE: info.language?.id,
    OLD_GENERATED_VIRAL_POST: old,
  };
  const result = await callLLM(prompt, promptInput);
  console.log(result);
  if (result) {
    // take only content inside <bio></bio> tags
    return extractViralPostDraft(result);
  }
};
